import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { addToSharedList, createRental } from "@/lib/api";
import { toast } from "sonner";
import { RENTAL } from "@/constants/testIds";

const initial = {
  title: "",
  rent: "",
  bedrooms: "",
  bathrooms: "",
  sqft: "",
  city: "",
  address: "",
  source_url: "",
  image_url: "",
  garage: false,
  parking: false,
  pet_friendly: false,
  furnished: false,
  laundry: "",
  available_date: null,
  notes: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
};

export const ManualAddDialog = ({ open, onOpenChange, onCreated, shareToken }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const payload = {
        ...form,
        rent: form.rent === "" ? null : Number(form.rent),
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        sqft: form.sqft === "" ? null : Number(form.sqft),
        laundry: form.laundry || null,
        available_date: form.available_date
          ? format(form.available_date, "yyyy-MM-dd")
          : null,
      };
      const r = shareToken
        ? await addToSharedList(shareToken, payload)
        : await createRental(payload);
      toast.success("Rental added");
      setForm(initial);
      onCreated?.(r);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add rental");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={RENTAL.manualDialog}
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#F9F8F6] border-stone-200"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-medium tracking-tight">
            Add a rental manually
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Fill the details you already know. Leave the rest blank.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Title</Label>
            <Input
              data-testid={RENTAL.manualTitle}
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Sunny 2BR near park"
              className="mt-2 bg-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Monthly Rent ($)">
              <Input
                type="number"
                data-testid={RENTAL.manualRent}
                value={form.rent}
                onChange={(e) => update("rent", e.target.value)}
                className="mt-2 bg-white"
              />
            </Field>
            <Field label="Sqft">
              <Input
                type="number"
                data-testid={RENTAL.manualSqft}
                value={form.sqft}
                onChange={(e) => update("sqft", e.target.value)}
                className="mt-2 bg-white"
              />
            </Field>
            <Field label="Bedrooms">
              <Input
                type="number"
                step="0.5"
                data-testid={RENTAL.manualBedrooms}
                value={form.bedrooms}
                onChange={(e) => update("bedrooms", e.target.value)}
                className="mt-2 bg-white"
              />
            </Field>
            <Field label="Bathrooms">
              <Input
                type="number"
                step="0.5"
                data-testid={RENTAL.manualBathrooms}
                value={form.bathrooms}
                onChange={(e) => update("bathrooms", e.target.value)}
                className="mt-2 bg-white"
              />
            </Field>
            <Field label="City">
              <Input
                data-testid={RENTAL.manualCity}
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                className="mt-2 bg-white"
              />
            </Field>
            <Field label="Address">
              <Input
                data-testid={RENTAL.manualAddress}
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                className="mt-2 bg-white"
              />
            </Field>
          </div>

          <Field label="Source URL">
            <Input
              data-testid={RENTAL.manualSourceUrl}
              value={form.source_url}
              onChange={(e) => update("source_url", e.target.value)}
              placeholder="https://…"
              className="mt-2 bg-white"
            />
          </Field>

          <Field label="Image URL">
            <Input
              data-testid={RENTAL.manualImageUrl}
              value={form.image_url}
              onChange={(e) => update("image_url", e.target.value)}
              placeholder="https://…/photo.jpg"
              className="mt-2 bg-white"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Laundry">
              <Select
                value={form.laundry}
                onValueChange={(v) => update("laundry", v)}
              >
                <SelectTrigger
                  className="mt-2 bg-white"
                  data-testid={RENTAL.manualLaundry}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-unit">In-unit</SelectItem>
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="hookups">Hookups</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Available Date">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={RENTAL.manualAvailableDate}
                    className="mt-2 w-full justify-start bg-white font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.available_date
                      ? format(form.available_date, "PPP")
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white">
                  <Calendar
                    mode="single"
                    selected={form.available_date}
                    onSelect={(d) => update("available_date", d)}
                  />
                </PopoverContent>
              </Popover>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <ToggleRow
              label="Garage"
              checked={form.garage}
              onChange={(v) => update("garage", v)}
              testid={RENTAL.manualGarage}
            />
            <ToggleRow
              label="Parking"
              checked={form.parking}
              onChange={(v) => update("parking", v)}
              testid={RENTAL.manualParking}
            />
            <ToggleRow
              label="Pet Friendly"
              checked={form.pet_friendly}
              onChange={(v) => update("pet_friendly", v)}
              testid={RENTAL.manualPetFriendly}
            />
            <ToggleRow
              label="Furnished"
              checked={form.furnished}
              onChange={(v) => update("furnished", v)}
              testid={RENTAL.manualFurnished}
            />
          </div>

          <div className="pt-2">
            <p className="overline mb-2">Contact</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Name">
                <Input
                  data-testid={RENTAL.manualContactName}
                  value={form.contact_name}
                  onChange={(e) => update("contact_name", e.target.value)}
                  placeholder="Landlord / agent"
                  className="mt-2 bg-white"
                />
              </Field>
              <Field label="Phone">
                <Input
                  type="tel"
                  data-testid={RENTAL.manualContactPhone}
                  value={form.contact_phone}
                  onChange={(e) => update("contact_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-2 bg-white"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  data-testid={RENTAL.manualContactEmail}
                  value={form.contact_email}
                  onChange={(e) => update("contact_email", e.target.value)}
                  placeholder="name@example.com"
                  className="mt-2 bg-white"
                />
              </Field>
            </div>
          </div>

          <Field label="Notes">
            <Textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="mt-2 bg-white"
              rows={3}
            />
          </Field>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid={RENTAL.manualSubmit}
              className="bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save Rental"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="overline">{label}</Label>
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-4 py-3">
    <span className="text-sm font-medium text-stone-700">{label}</span>
    <Switch
      data-testid={testid}
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#4A5D4E]"
    />
  </div>
);

export default ManualAddDialog;
